import React, { useState, useEffect, useRef } from 'react'
import { MessageCircle, Send, User, Clock, RefreshCw } from 'lucide-react'
import { supabase } from '../../services/supabase'
import { useAuth } from '../../context/AuthContext'

const UserChat = () => {
  const [conversations, setConversations] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const messagesEndRef = useRef(null)
  const { user } = useAuth()

  useEffect(() => {
    loadConversations()
    
    // Set up real-time subscription for all messages
    const channel = supabase
      .channel('admin_chat')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages'
        },
        (payload) => {
          console.log('Change received!', payload)
          loadConversations()
          if (selectedUserId) {
            loadMessages(selectedUserId)
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  useEffect(() => {
    if (selectedUserId) {
      loadMessages(selectedUserId)
    }
  }, [selectedUserId])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const loadConversations = async () => {
    try {
      // Get unique user conversations with their latest message
      const { data, error } = await supabase
        .from('chat_messages')
        .select('user_id, user_name, user_email, created_at, sender, read')
        .order('created_at', { ascending: false })

      if (error) throw error

      // Group by user_id and get the latest info
      const userMap = new Map()
      data?.forEach(msg => {
        if (!userMap.has(msg.user_id)) {
          userMap.set(msg.user_id, {
            user_id: msg.user_id,
            user_name: msg.user_name,
            user_email: msg.user_email,
            last_message_time: msg.created_at,
            unread_count: 0
          })
        }
        
        // Count unread messages from users
        if (msg.sender === 'user' && !msg.read) {
          const user = userMap.get(msg.user_id)
          user.unread_count++
        }
      })

      setConversations(Array.from(userMap.values()))
    } catch (error) {
      console.error('Error loading conversations:', error)
    }
  }

  const loadMessages = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true })

      if (error) throw error

      setMessages(data || [])
      
      // Mark user messages as read
      await supabase
        .from('chat_messages')
        .update({ read: true })
        .eq('user_id', userId)
        .eq('sender', 'user')
        .eq('read', false)
      
      // Refresh conversations to update unread counts
      await loadConversations()
    } catch (error) {
      console.error('Error loading messages:', error)
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || !selectedUserId || loading) return

    setLoading(true)
    try {
      const selectedConv = conversations.find(c => c.user_id === selectedUserId)
      
      const { error } = await supabase
        .from('chat_messages')
        .insert([
          {
            user_id: selectedUserId,
            user_name: selectedConv.user_name,
            user_email: selectedConv.user_email,
            message: newMessage.trim(),
            sender: 'admin',
            read: false
          }
        ])

      if (error) throw error

      setNewMessage('')
      await loadMessages(selectedUserId)
    } catch (error) {
      console.error('Error sending message:', error)
      alert('Failed to send message: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadConversations()
    if (selectedUserId) {
      await loadMessages(selectedUserId)
    }
    setRefreshing(false)
  }

  const selectedConversation = conversations.find(c => c.user_id === selectedUserId)

  return (
    <div className="h-[calc(100vh-12rem)] bg-white dark:bg-dark-800 rounded-lg shadow-soft overflow-hidden">
      <div className="grid grid-cols-12 h-full">
        {/* User List Sidebar */}
        <div className="col-span-4 border-r border-gray-200 dark:border-dark-700 overflow-y-auto">
          <div className="p-4 border-b border-gray-200 dark:border-dark-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Conversations
            </h2>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-dark-700">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No conversations yet</p>
              </div>
            ) : (
              conversations.map((conv) => {
                const isSelected = selectedUserId === conv.user_id

                return (
                  <button
                    key={conv.user_id}
                    onClick={() => setSelectedUserId(conv.user_id)}
                    className={`w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors ${
                      isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {conv.user_name}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {conv.user_email}
                          </p>
                        </div>
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(conv.last_message_time).toLocaleString()}
                    </p>
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="col-span-8 flex flex-col">
          {selectedUserId ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 dark:border-dark-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {selectedConversation?.user_name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedConversation?.user_email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-lg p-3 ${
                        message.sender === 'admin'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-dark-700 text-gray-900 dark:text-white'
                      }`}
                    >
                      <p className="text-sm font-semibold mb-1 opacity-75">
                        {message.sender === 'admin' ? 'You (Admin)' : message.user_name}
                      </p>
                      <p className="text-sm">{message.message}</p>
                      <p className={`text-xs mt-1 ${message.sender === 'admin' ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
                        {new Date(message.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 dark:border-dark-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1 input"
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || loading}
                    className="btn btn-primary"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select a conversation to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserChat